import type { ReactNode, JSX} from "react";
import { cn } from "./cn";
import { Row } from "./Row";
import { Stack } from "./Stack";

export interface EntityHeaderProps {
  /** Avatar element (Avatar component or custom) */
  readonly avatar: ReactNode;
  /** Primary name / title */
  readonly name: string;
  /** Subtitle line (e.g. "CTO at TechVentures · Berlin") */
  readonly subtitle?: string;
  /** Meta line (e.g. phone · handle · date) */
  readonly meta?: string;
  /** Tags rendered below the meta line */
  readonly tags?: ReactNode;
  /** Action buttons (top-right corner, e.g. three-dot menu) */
  readonly actions?: ReactNode;
  /** Extra className */
  readonly className?: string;
}

/**
 * Generic entity detail header with avatar, name, subtitle, meta, and tags.
 * Used for contacts, companies, projects, and other entity detail pages.
 * Layout: avatar on the left, stacked info to the right, actions top-right.
 */
export function EntityHeader({
  avatar,
  name,
  subtitle,
  meta,
  tags,
  actions,
  className,
}: EntityHeaderProps): JSX.Element {
  return (
    <div
      className={cn("flex gap-4 px-6 py-5", className)}
      style={{ boxShadow: "inset 0 -1px 0 0 var(--color-edge)" }}
    >
      <div className="shrink-0 pt-0.5">{avatar}</div>
      <Stack gap={1.5} flex1>
        <Row align="center" justify="between">
          <span className="text-lg font-bold text-content">{name}</span>
          {actions ? <div className="shrink-0">{actions}</div> : null}
        </Row>
        {subtitle ? (
          <span className="text-[13px] text-[#8A949A]">{subtitle}</span>
        ) : null}
        {meta ? (
          <span className="text-xs text-[#5A6A72]">{meta}</span>
        ) : null}
        {tags ? <Row gap={1.5} wrap className="mt-1.5">{tags}</Row> : null}
      </Stack>
    </div>
  );
}
